/**
 * 상담 효과 통계 위젯
 * Consultation Effectiveness Statistics Widget
 *
 * 전체 상담 효과 평균 점수, 상담 유형별 효과 비교, 월별 추이 그래프 표시
 */

import React, { useState, useEffect } from 'react';
import { counselingRecordService } from '../../learning/services/CounselingRecordService';
import './ConsultationEffectivenessWidget.css';

interface ConsultationEffectivenessWidgetProps {
  organizationId: string;
  monthsToShow?: number;
}

interface EffectivenessStats {
  total_consultations: number;
  evaluated_consultations: number;
  average_score: number;
  by_type: {
    type: string;
    count: number;
    average_score: number;
  }[];
  monthly_trend: {
    month: string;
    count: number;
    average_score: number;
  }[];
}

export const ConsultationEffectivenessWidget: React.FC<ConsultationEffectivenessWidgetProps> = ({
  organizationId,
  monthsToShow = 6,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [stats, setStats] = useState<EffectivenessStats | null>(null);

  useEffect(() => {
    if (organizationId) {
      loadStats();
    }
  }, [organizationId, monthsToShow]);

  const loadStats = async () => {
    try {
      setLoading(true);

      // 조직의 모든 상담 기록 조회
      const records = await counselingRecordService.getByOrganization(organizationId);

      // 효과 측정이 완료된 상담만 필터링
      const evaluatedRecords = records.filter(
        (r) => r.effectiveness_score !== null && r.effectiveness_score !== undefined
      );

      // 전체 평균 점수 계산
      const averageScore =
        evaluatedRecords.length > 0
          ? evaluatedRecords.reduce((sum, r) => sum + (r.effectiveness_score || 0), 0) /
            evaluatedRecords.length
          : 0;

      // 상담 유형별 통계
      const typeStats = new Map<string, { count: number; total_score: number }>();
      evaluatedRecords.forEach((r) => {
        const type = r.counseling_type || 'unknown';
        if (!typeStats.has(type)) {
          typeStats.set(type, { count: 0, total_score: 0 });
        }
        const stat = typeStats.get(type)!;
        stat.count++;
        stat.total_score += r.effectiveness_score || 0;
      });

      const byType = Array.from(typeStats.entries()).map(([type, stat]) => ({
        type,
        count: stat.count,
        average_score: stat.total_score / stat.count,
      }));

      // 월별 추이 계산
      const now = new Date();
      const monthlyData = new Map<string, { count: number; total_score: number }>();

      for (let i = monthsToShow - 1; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        monthlyData.set(monthKey, { count: 0, total_score: 0 });
      }

      evaluatedRecords.forEach((r) => {
        const date = new Date(r.counseling_date);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

        if (monthlyData.has(monthKey)) {
          const data = monthlyData.get(monthKey)!;
          data.count++;
          data.total_score += r.effectiveness_score || 0;
        }
      });

      const monthlyTrend = Array.from(monthlyData.entries()).map(([month, data]) => ({
        month,
        count: data.count,
        average_score: data.count > 0 ? data.total_score / data.count : 0,
      }));

      setStats({
        total_consultations: records.length,
        evaluated_consultations: evaluatedRecords.length,
        average_score: averageScore,
        by_type: byType,
        monthly_trend: monthlyTrend,
      });
    } catch (error) {
      console.error('상담 효과 통계 로딩 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score: number): string => {
    if (score >= 70) return '#4caf50';
    if (score >= 50) return '#ff9800';
    return '#f44336';
  };

  const getScoreLabel = (score: number): string => {
    if (score >= 70) return '효과적';
    if (score >= 50) return '보통';
    return '개선 필요';
  };

  const getTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      regular: '정기 상담',
      special: '특별 상담',
      parent: '학부모 상담',
      academic: '학업 상담',
      counseling: '심리 상담',
      unknown: '기타',
    };
    return labels[type] || type;
  };

  const formatMonth = (monthKey: string): string => {
    const [year, month] = monthKey.split('-');
    return `${year.slice(2)}년 ${month}월`;
  };

  if (loading) {
    return (
      <div className="effectiveness-widget loading">
        <div className="loading-spinner">로딩 중...</div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="effectiveness-widget error">
        <p>데이터를 불러오지 못했습니다.</p>
      </div>
    );
  }

  const evaluationRate =
    stats.total_consultations > 0
      ? (stats.evaluated_consultations / stats.total_consultations) * 100
      : 0;

  return (
    <div className="effectiveness-widget">
      <div className="widget-header">
        <h2 className="widget-title">📊 상담 효과 통계</h2>
      </div>

      {/* 전체 요약 */}
      <div className="overall-stats">
        <div className="stat-card primary">
          <div className="stat-label">평균 효과 점수</div>
          <div className="stat-value" style={{ color: getScoreColor(stats.average_score) }}>
            {stats.average_score.toFixed(1)}점
          </div>
          <div className="stat-sublabel">{getScoreLabel(stats.average_score)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">총 상담 건수</div>
          <div className="stat-value">{stats.total_consultations}건</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">평가 완료</div>
          <div className="stat-value">{stats.evaluated_consultations}건</div>
          <div className="stat-sublabel">({evaluationRate.toFixed(0)}%)</div>
        </div>
      </div>

      {/* 상담 유형별 효과 */}
      {stats.by_type.length > 0 && (
        <div className="type-comparison">
          <h3 className="section-title">상담 유형별 효과 비교</h3>
          <div className="type-list">
            {stats.by_type.map((item, index) => (
              <div key={index} className="type-item">
                <div className="type-info">
                  <span className="type-name">{getTypeLabel(item.type)}</span>
                  <span className="type-count">{item.count}건</span>
                </div>
                <div className="type-score-bar">
                  <div
                    className="score-fill"
                    style={{
                      width: `${item.average_score}%`,
                      backgroundColor: getScoreColor(item.average_score),
                    }}
                  />
                </div>
                <div className="type-score">{item.average_score.toFixed(1)}점</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 월별 추이 */}
      {stats.monthly_trend.length > 0 && (
        <div className="monthly-trend">
          <h3 className="section-title">월별 추이</h3>
          <div className="trend-chart">
            {stats.monthly_trend.map((item, index) => (
              <div key={index} className="trend-item">
                <div className="trend-label">{formatMonth(item.month)}</div>
                <div
                  className="trend-bar"
                  style={{
                    height: item.count > 0 ? `${(item.average_score / 100) * 100}%` : '4px',
                    backgroundColor:
                      item.count > 0 ? getScoreColor(item.average_score) : '#e0e0e0',
                  }}
                  title={`${item.count}건, 평균 ${item.average_score.toFixed(1)}점`}
                />
                <div className="trend-value">
                  {item.count > 0 ? item.average_score.toFixed(0) : '-'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 데이터 없음 */}
      {stats.evaluated_consultations === 0 && (
        <div className="no-data">
          <p>아직 평가된 상담 기록이 없습니다.</p>
          <p className="help-text">상담 2주 후 자동으로 효과가 측정됩니다.</p>
        </div>
      )}
    </div>
  );
};

export default ConsultationEffectivenessWidget;
